import { validatedDisplayView } from "../i18n/display-view.js";

/**
 * Wire → view for a build (customer-portal review 2026-09-25, #28, #29, #30, #32, #130).
 *
 * Two reads carry a build: `getVoiceAgent`'s `latest_build` (the agent page leads with a failed
 * build's reason and offers Try again / Replace document) and `getBuildJob` (the Building screen
 * reads it on load, so a reload or a second tab shows the real state instead of "0 % · queued").
 * Like every reader here, a missing field raises rather than defaulting: a console that rendered
 * "no failure" for a failure it could not read would say the build is fine.
 *
 * `failure.message` is the owner's sentence; the API never sends the operator's technical detail.
 */
import { readArray, readBoolean, readRecord, readText, readWholeNumber } from "./decode.js";
import { readNullableText } from "./conversation-view.js";

export type BuildState = "queued" | "running" | "succeeded" | "failed";

/** `platform` — WeTalk's side, only "Try again" can help; `input` — the owner's to change. */
export type BuildFault = "platform" | "input";

export interface BuildFailureView {
  readonly stepCode: string | null;
  readonly errorCode: string | null;
  /** The owner's sentence. `null` only on a build that failed before the API stored one. */
  readonly message: string | null;
  /** `null` on a build that failed before the fault was recorded (migration 0032). */
  readonly fault: BuildFault | null;
}

/** A VoiceAgent's newest build, as its page shows it. */
export interface LatestBuildView {
  readonly buildJobId: string;
  readonly agentVersionLabel: string;
  readonly state: BuildState;
  /** Queued and never started: the build waits for documents (a "Replace document" in progress). */
  readonly waitingForDocument: boolean;
  readonly percent: number;
  readonly stepLabel: string | null;
  readonly failure: (BuildFailureView & { readonly stepLabel: string | null }) | null;
}

export type DocumentCheckOutcome = "valid" | "invalid" | "no_draft" | "budget_spent" | "truncated" | "refused" | "answered";

export interface DocumentCheckView {
  readonly round: number;
  readonly outcome: DocumentCheckOutcome;
}

export interface BuildJobStepView {
  readonly ordinal: number;
  readonly code: string;
  readonly label: string;
  readonly state: string;
  readonly detail: string | null;
  /** When the step started, as epoch milliseconds — the screen counts the seconds from it (#30). */
  readonly startedAtMs: number | null;
}

/** One build as it stands now (`getBuildJob`). */
export interface BuildJobView {
  readonly buildJobId: string;
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  readonly agentVersionLabel: string;
  readonly state: BuildState;
  readonly waitingForDocument: boolean;
  readonly percent: number;
  readonly stepCode: string | null;
  readonly step: readonly BuildJobStepView[];
  readonly documentCheck: DocumentCheckView | null;
  readonly failure: BuildFailureView | null;
  /** The VoiceAgent has a live version now — a first build publishes itself. */
  readonly voiceAgentLive: boolean;
  /** Any number or endpoint reaches it; when not, the next step is to connect one (#131). */
  readonly reachable: boolean;
}

const BUILD_STATE: readonly BuildState[] = ["queued", "running", "succeeded", "failed"];

function readBuildState(value: unknown, path: string): BuildState {
  const raw = readText(value, path);
  const found = BUILD_STATE.find((one) => one === raw);
  if (found === undefined) throw new Error(`Unknown build state "${raw}" at ${path}.`);
  return found;
}

function readFault(value: unknown, path: string): BuildFault | null {
  const raw = readNullableText(value, path);
  if (raw === null) return null;
  if (raw !== "platform" && raw !== "input") throw new Error(`Unknown build fault "${raw}" at ${path}.`);
  return raw;
}

function failureOf(value: unknown, path: string): BuildFailureView | null {
  if (value === null) return null;
  const one = readRecord(value, path);
  return {
    stepCode: readNullableText(one["step_code"], `${path}.step_code`),
    errorCode: readNullableText(one["error_code"], `${path}.error_code`),
    message: readNullableText(one["message"], `${path}.message`),
    fault: readFault(one["fault"], `${path}.fault`),
  };
}

export function latestBuildOfWire(value: unknown, path: string): LatestBuildView | null {
  if (value === null) return null;
  const one = readRecord(value, path);
  const failure = failureOf(one["failure"], `${path}.failure`);
  return {
    buildJobId: readText(one["build_job_id"], `${path}.build_job_id`),
    agentVersionLabel: readText(one["agent_version_label"], `${path}.agent_version_label`),
    state: readBuildState(one["state"], `${path}.state`),
    waitingForDocument: readBoolean(one["waiting_for_document"], `${path}.waiting_for_document`),
    percent: readWholeNumber(one["percent"], `${path}.percent`),
    stepLabel: readNullableText(one["step_label"], `${path}.step_label`),
    failure:
      failure === null
        ? null
        : {
            ...failure,
            stepLabel: readNullableText(
              readRecord(one["failure"], `${path}.failure`)["step_label"],
              `${path}.failure.step_label`,
            ),
          },
  };
}

function epochOf(value: unknown, path: string): number | null {
  const raw = readNullableText(value, path);
  if (raw === null) return null;
  const at = Date.parse(raw);
  if (Number.isNaN(at)) throw new Error(`"${raw}" at ${path} is not a timestamp.`);
  return at;
}

function documentCheckOf(value: unknown, path: string): DocumentCheckView | null {
  // Additive API field: an older API still supports the indeterminate progress view.
  if (value === null || value === undefined) return null;
  const one = readRecord(value, path);
  const raw = readText(one["outcome"], `${path}.outcome`);
  const outcome: readonly DocumentCheckOutcome[] = ["valid", "invalid", "no_draft", "budget_spent", "truncated", "refused", "answered"];
  const found = outcome.find((item) => item === raw);
  if (found === undefined) throw new Error(`Unknown document check outcome "${raw}" at ${path}.`);
  return { round: readWholeNumber(one["round"], `${path}.round`), outcome: found };
}

export function buildJobOfWire(value: unknown, path: string): BuildJobView {
  const one = readRecord(value, path);
  return {
    buildJobId: readText(one["build_job_id"], `${path}.build_job_id`),
    voiceAgentId: readText(one["voice_agent_id"], `${path}.voice_agent_id`),
    voiceAgentName: readText(one["voice_agent_name"], `${path}.voice_agent_name`),
    agentVersionLabel: readText(one["agent_version_label"], `${path}.agent_version_label`),
    state: readBuildState(one["state"], `${path}.state`),
    waitingForDocument: readBoolean(one["waiting_for_document"], `${path}.waiting_for_document`),
    percent: readWholeNumber(one["percent"], `${path}.percent`),
    stepCode: readNullableText(one["step_code"], `${path}.step_code`),
    step: readArray(one["step"], `${path}.step`).map((row, index): BuildJobStepView => {
      const at = `${path}.step[${index}]`;
      const step = readRecord(row, at);
      return validatedDisplayView({
        ordinal: readWholeNumber(step["ordinal"], `${at}.ordinal`),
        code: readText(step["code"], `${at}.code`),
        get label() { return readText(step["label"], `${at}.label`); },
        state: readText(step["state"], `${at}.state`),
        detail: readNullableText(step["detail"], `${at}.detail`),
        startedAtMs: epochOf(step["started_at"], `${at}.started_at`),
      });
    }),
    documentCheck: documentCheckOf(one["document_check"], `${path}.document_check`),
    failure: failureOf(one["failure"], `${path}.failure`),
    voiceAgentLive: readBoolean(one["voice_agent_live"], `${path}.voice_agent_live`),
    reachable: readBoolean(one["reachable"], `${path}.reachable`),
  };
}
